import { ComponentProps, forwardRef, useEffect, useRef } from 'react';

import { mergeRefs, Stack } from '@eneco/sparky';
import { getClassNames } from '@eneco/sparky/util/css';

import {
  spinnerIconContainerClass,
  styledFooterRecipe,
  StyledFooterVariants,
  styledHeaderClass,
  styledSectionRecipe,
  StyledSectionVariants,
  styledStackClass,
} from './Layout.css';

export const StyledHeader = forwardRef<HTMLElement, ComponentProps<'header'>>(({ className, ...props }, ref) => {
  const headerRef = useRef<HTMLElement>(null);
  const mergedRef = mergeRefs([ref, headerRef]);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const setHeaderHeight = () => {
      const height = header.getBoundingClientRect().height;
      if (!height) return;
      document.documentElement.style.setProperty('--flow-header-height', `${Math.ceil(height)}px`);
    };

    setHeaderHeight();
    const observer = new ResizeObserver(setHeaderHeight);
    observer.observe(header);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--flow-header-height');
    };
  }, []);

  return <header ref={mergedRef} className={getClassNames(styledHeaderClass, className)} {...props} />;
});
StyledHeader.displayName = 'StyledHeader';

interface StyledFooterProps extends ComponentProps<'footer'> {
  isLoading?: StyledFooterVariants['isLoading'];
}

export const StyledFooter = forwardRef<HTMLElement, StyledFooterProps>(({ className, isLoading, ...props }, ref) => (
  <footer
    ref={ref}
    className={getClassNames(styledFooterRecipe({ isLoading }), className)}
    data-is-loading={isLoading}
    {...props}
  />
));
StyledFooter.displayName = 'StyledFooter';

interface StyledSectionProps extends ComponentProps<'section'> {
  variant?: StyledSectionVariants['variant'];
  withDelayedAnimation?: StyledSectionVariants['withDelayedAnimation'];
}

export const StyledSection = forwardRef<HTMLElement, StyledSectionProps>(
  ({ className, variant, withDelayedAnimation, ...props }, ref) => (
    <section
      ref={ref}
      className={getClassNames(styledSectionRecipe({ variant, withDelayedAnimation }), className)}
      {...props}
    />
  ),
);
StyledSection.displayName = 'StyledSection';

export const StyledStack = (props: ComponentProps<typeof Stack>) => (
  <Stack className={styledStackClass} {...props} />
);

export const SpinnerIconContainer = (props: ComponentProps<typeof Stack>) => (
  <Stack className={spinnerIconContainerClass} {...props} />
);

/**
 * @deprecated Body background color is handled via static global styles in Layout.css.ts.
 */
export const setBodyBackgroundColor = (_bodyClassName: string): void => {
  // Logic migrated to globalStyle in Layout.css.ts
};
